/**
 * useHomeMessages.ts
 *
 * Screen-level hook for the Home screen.
 * Returns at most 1 message (T3, T4 A2HS, or GLP-1 meal nudge).
 *
 * Handles:
 * - Platform detection for A2HS copy
 * - Firestore flag write on A2HS dismiss
 * - GLP-1 dose day nudge (delegates to existing useGLP1DoseDayNudge for dose day)
 *
 * Usage:
 *   const { message, dismiss } = useHomeMessages(user?.uid);
 *
 * Target: src/hooks/useHomeMessages.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { evaluateHomeMessage } from '../services/messaging/messagingEngine';
import { getUserProfile, saveUserProfile } from '../services/userProfile.service';
import type { ResolvedMessage } from '../services/messaging/messaging.types';

// ── Platform detection ────────────────────────────────────────────────────────

type Platform = 'ios_safari' | 'ios_nonsafari' | 'android' | 'other';

function detectPlatform(): Platform {
  try {
    const ua = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);

    if (isIOS && isSafari) return 'ios_safari';
    if (isIOS && !isSafari) return 'ios_nonsafari';
    if (isAndroid) return 'android';
    return 'other';
  } catch {
    return 'other';
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseHomeMessagesResult {
  /** The message to display, or null if none qualifies */
  message: ResolvedMessage | null;
  /** Call when the user dismisses the message. Handles Firestore writes for A2HS. */
  dismiss: () => void;
}

export function useHomeMessages(
  userId: string | undefined,
): UseHomeMessagesResult {
  const [message, setMessage] = useState<ResolvedMessage | null>(null);
  const [hasSeenA2HS, setHasSeenA2HS] = useState<boolean>(true); // default to true to avoid flash
  const [hasEverLogged, setHasEverLogged] = useState<boolean>(false);
  const [platform] = useState<Platform>(() => detectPlatform());

  // Load A2HS flag and ever-logged state from profile
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function loadProfile() {
      try {
        const profile = await getUserProfile(userId!);
        if (!cancelled) {
          setHasSeenA2HS(profile?.hasSeenA2HS ?? false);
          // "ever logged" — use onboarding_complete as a proxy
          // (if onboarding is complete, they've been through setup and likely logged)
          setHasEverLogged(profile?.onboarding_complete ?? false);
        }
      } catch {
        if (!cancelled) {
          setHasSeenA2HS(true); // fail safe — don't show A2HS if we can't load profile
        }
      }
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [userId]);

  // Evaluate message
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    async function evaluate() {
      try {
        const result = await evaluateHomeMessage(userId!, {
          hasSeenA2HS,
          hasEverLogged,
          platform,
        });
        if (!cancelled) {
          setMessage(result);
        }
      } catch (error) {
        console.error('[useHomeMessages] Evaluation failed:', error);
        if (!cancelled) setMessage(null);
      }
    }

    evaluate();
    return () => { cancelled = true; };
  }, [userId, hasSeenA2HS, hasEverLogged, platform]);

  const dismiss = useCallback(() => {
    if (!message) return;

    // A2HS: write Firestore flag on dismiss so it never shows again
    if (message.id === 'add_to_home_screen' && userId) {
      setHasSeenA2HS(true);
      saveUserProfile(userId, { hasSeenA2HS: true }).catch(error => {
        console.error('[useHomeMessages] Failed to persist A2HS flag:', error);
      });
    }

    setMessage(null);
  }, [message, userId]);

  return { message, dismiss };
}
